const AVAILABILITY_PARAM = 'filter.v.availability';
const IN_STOCK = '1';
const OUT_OF_STOCK = '0';
const VALID_PHASES = new Set([IN_STOCK, OUT_OF_STOCK]);
const CONTROL_SELECTOR = '[data-oz-stock-continuation]';
const RETURN_KEY_PREFIX = 'oz-stock-last:return:';

let renderFrame = 0;
let resultsObserver = null;
let observedSection = null;

const currentUrl = () => new URL(window.location.href);

function availabilityInputs() {
  return Array.from(document.querySelectorAll(`input[name="${AVAILABILITY_PARAM}"]`));
}

function supportsAvailabilityFilter() {
  return availabilityInputs().length > 0;
}

function currentPhase() {
  const values = currentUrl().searchParams.getAll(AVAILABILITY_PARAM).filter((value) => VALID_PHASES.has(value));
  return values.length === 1 ? values[0] : null;
}

function normaliseCollectionUrl() {
  if (!supportsAvailabilityFilter()) return false;

  const url = currentUrl();
  const allValues = url.searchParams.getAll(AVAILABILITY_PARAM);
  const validValues = allValues.filter((value) => VALID_PHASES.has(value));

  if (allValues.length === 1 && validValues.length === 1) return false;

  url.searchParams.delete(AVAILABILITY_PARAM);
  url.searchParams.set(AVAILABILITY_PARAM, IN_STOCK);
  url.searchParams.delete('page');
  window.location.replace(url.toString());
  return true;
}

function phaseHasProducts(phase) {
  const inputs = availabilityInputs().filter((input) => input.value === phase);
  return inputs.some((input) => !input.disabled);
}

function phaseUrl(phase, page = 1) {
  const url = currentUrl();
  url.searchParams.delete(AVAILABILITY_PARAM);
  url.searchParams.set(AVAILABILITY_PARAM, phase);

  if (page > 1) {
    url.searchParams.set('page', String(page));
  } else {
    url.searchParams.delete('page');
  }

  return url.toString();
}

function hasNextPage(results) {
  const pagination = results.querySelector('.pagination');
  if (!pagination) return false;

  const lastItem = pagination.querySelector('.pagination__item:last-child');
  return Boolean(lastItem?.querySelector('a.pagination__link--arrow'));
}

function createContinuation(kind) {
  const nav = document.createElement('nav');
  nav.className = `oz-stock-continuation oz-stock-continuation--${kind}`;
  nav.dataset.ozStockContinuation = kind;
  nav.setAttribute('aria-label', 'Product availability pages');

  const link = document.createElement('a');
  link.className = 'oz-stock-continuation__link';

  if (kind === 'unavailable') {
    link.href = phaseUrl(OUT_OF_STOCK);
    link.dataset.ozStockTarget = OUT_OF_STOCK;
    link.innerHTML =
      '<span><strong>Continue browsing</strong><small>View products that are currently unavailable</small></span><span class="oz-stock-continuation__arrow" aria-hidden="true">→</span>';
  } else {
    link.href = phaseUrl(IN_STOCK);
    link.dataset.ozStockTarget = IN_STOCK;
    link.innerHTML =
      '<span class="oz-stock-continuation__arrow" aria-hidden="true">←</span><span><strong>Back to available products</strong><small>Return to products ready to order</small></span>';
  }

  nav.append(link);
  return nav;
}

function renderContinuation() {
  renderFrame = 0;

  const results = document.querySelector('#ResultsList');
  if (!results || !supportsAvailabilityFilter()) return;

  const phase = currentPhase();
  if (!phase) return;

  const grid = results.querySelector('.product-grid');
  if (!grid) return;

  let desiredKind = null;

  if (phase === IN_STOCK && !hasNextPage(results) && phaseHasProducts(OUT_OF_STOCK)) {
    desiredKind = 'unavailable';
  } else if (
    phase === OUT_OF_STOCK &&
    Number(currentUrl().searchParams.get('page') || 1) === 1 &&
    phaseHasProducts(IN_STOCK)
  ) {
    desiredKind = 'available';
  }

  const existing = results.querySelector(CONTROL_SELECTOR);

  if (!desiredKind) {
    existing?.remove();
    return;
  }

  if (existing?.dataset.ozStockContinuation === desiredKind) return;
  existing?.remove();

  const control = createContinuation(desiredKind);
  const pagination = results.querySelector('.pagination');
  const host = grid.parentElement;

  if (!host) return;

  if (desiredKind === 'unavailable') {
    if (pagination) pagination.insertAdjacentElement('afterend', control);
    else host.append(control);
  } else {
    grid.insertAdjacentElement('beforebegin', control);
  }
}

function scheduleRender() {
  if (renderFrame) return;
  renderFrame = window.requestAnimationFrame(renderContinuation);
}

function observeResultsSection() {
  const section = document.querySelector('#ResultsList')?.closest('.shopify-section');
  if (!section || section === observedSection) return;

  resultsObserver?.disconnect();
  observedSection = section;
  resultsObserver = new MutationObserver((mutations) => {
    const meaningfulChange = mutations.some(
      (mutation) =>
        mutation.type === 'childList' &&
        !mutation.target.closest?.('.oz-stock-continuation') &&
        Array.from(mutation.addedNodes).some(
          (node) => !(node instanceof Element) || !node.matches?.('.oz-stock-continuation')
        )
    );

    if (meaningfulChange) scheduleRender();
  });
  resultsObserver.observe(section, { childList: true, subtree: true });
}

function patchFacetParameters() {
  customElements.whenDefined('facets-form-component').then(() => {
    const FacetsForm = customElements.get('facets-form-component');
    if (!FacetsForm || FacetsForm.prototype.ozStockLastPatched) return;

    const originalCreateURLParameters = FacetsForm.prototype.createURLParameters;

    FacetsForm.prototype.createURLParameters = function createStockAwareURLParameters(formData) {
      const parameters = originalCreateURLParameters.call(this, formData);

      if (this.dataset.pageType !== 'collection') return parameters;

      const activePhase = currentPhase() || IN_STOCK;
      const submittedPhases = parameters
        .getAll(AVAILABILITY_PARAM)
        .filter((value) => VALID_PHASES.has(value));

      let nextPhase = activePhase;

      if (submittedPhases.length === 1) {
        nextPhase = submittedPhases[0];
      } else if (submittedPhases.length > 1) {
        nextPhase = submittedPhases.find((value) => value !== activePhase) || activePhase;
      }

      parameters.delete(AVAILABILITY_PARAM);
      parameters.set(AVAILABILITY_PARAM, nextPhase);
      return parameters;
    };

    Object.defineProperty(FacetsForm.prototype, 'ozStockLastPatched', {
      value: true,
      configurable: false,
      enumerable: false,
      writable: false,
    });
  });
}

function handleContinuationClick(event) {
  if (!(event.target instanceof Element)) return;

  const link = event.target.closest('[data-oz-stock-target]');
  if (!(link instanceof HTMLAnchorElement)) return;

  const target = link.dataset.ozStockTarget;
  if (!target || !VALID_PHASES.has(target)) return;

  event.preventDefault();

  const returnKey = `${RETURN_KEY_PREFIX}${window.location.pathname}`;

  if (target === OUT_OF_STOCK) {
    sessionStorage.setItem(returnKey, window.location.href);
    window.location.assign(phaseUrl(OUT_OF_STOCK));
    return;
  }

  const returnUrl = sessionStorage.getItem(returnKey);
  if (returnUrl) {
    try {
      const parsedReturnUrl = new URL(returnUrl);
      if (parsedReturnUrl.origin === window.location.origin && parsedReturnUrl.pathname === window.location.pathname) {
        window.location.assign(parsedReturnUrl.toString());
        return;
      }
    } catch {
      sessionStorage.removeItem(returnKey);
    }
  }

  window.location.assign(phaseUrl(IN_STOCK));
}

function handleCollectionUpdate(event) {
  const updatePromise = event.detail?.promise;

  if (updatePromise && typeof updatePromise.finally === 'function') {
    updatePromise.finally(() => {
      observeResultsSection();
      scheduleRender();
    });
  } else {
    window.setTimeout(() => {
      observeResultsSection();
      scheduleRender();
    }, 50);
  }
}

function initialise() {
  if (!document.querySelector('[data-template^="collection"]')) return;
  if (normaliseCollectionUrl()) return;

  patchFacetParameters();
  observeResultsSection();
  scheduleRender();

  document.addEventListener('click', handleContinuationClick);
  document.addEventListener('collection:update', handleCollectionUpdate);
  window.addEventListener('popstate', () => {
    if (normaliseCollectionUrl()) return;
    observeResultsSection();
    scheduleRender();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialise, { once: true });
} else {
  initialise();
}
